import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Star, BadgeCheck, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const StarIcons = ({ value, size = 16 }) => (
  <div className="flex">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} className={i < Math.round(value) ? "text-accent" : "text-slate-300"} fill="currentColor" />
    ))}
  </div>
);

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex gap-1" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          data-testid={`review-star-${n}`}
          className="p-0.5"
        >
          <Star size={26} className={n <= shown ? "text-accent" : "text-slate-300"} fill="currentColor" />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection({ book }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "", name: "", email: "" });

  const LIMIT = 5;

  const load = useCallback(async (pageNum = 1, append = false) => {
    try {
      const r = await api.get(`/books/${book.id}/reviews`, { params: { page: pageNum, limit: LIMIT } });
      setData(r.data);
      setItems((prev) => (append ? [...prev, ...(r.data.items || [])] : (r.data.items || [])));
      setPage(pageNum);
    } catch {
      toast.error("Could not load reviews");
    } finally {
      setLoading(false);
    }
  }, [book.id]);

  useEffect(() => { setLoading(true); load(1, false); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Please choose a star rating");
    if (form.body.trim().length < 5) return toast.error("Please write a few words about the book");
    setSubmitting(true);
    try {
      await api.post(`/books/${book.id}/reviews`, {
        rating: form.rating,
        title: form.title.trim() || null,
        body: form.body.trim(),
        ...(user ? {} : { name: form.name.trim(), email: form.email.trim() }),
      });
      toast.success("Thank you — your review is live.");
      setDialogOpen(false);
      setForm({ rating: 0, title: "", body: "", name: "", email: "" });
      load(1, false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your review");
    } finally {
      setSubmitting(false);
    }
  };

  const total = data?.total || 0;
  const avg = data?.avg_rating || 0;
  const breakdown = data?.breakdown || {};
  const hasMore = items.length < total;

  return (
    <section className="mt-8" data-testid="reviews-section">
      <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">Customer reviews</h2>

      <div className="grid md:grid-cols-12 gap-6">
        {/* Summary */}
        <div className="md:col-span-4 bg-white border border-slate-200 rounded-lg p-5 h-fit" data-testid="reviews-summary">
          {total > 0 ? (
            <>
              <div className="flex items-end gap-2">
                <span className="font-heading font-bold text-4xl text-slate-900" data-testid="reviews-avg">{avg.toFixed(1)}</span>
                <span className="text-sm text-slate-500 mb-1">out of 5</span>
              </div>
              <div className="mt-1"><StarIcons value={avg} /></div>
              <p className="mt-1 text-sm text-slate-500" data-testid="reviews-count">
                Based on {total} review{total === 1 ? "" : "s"}
              </p>
              <div className="mt-4 space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const n = breakdown[star] || 0;
                  const pct = total ? Math.round((n / total) * 100) : 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`reviews-bar-${star}`}>
                      <span className="w-7 shrink-0">{star} ★</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-9 text-right text-slate-500">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-600" data-testid="reviews-empty">
              No reviews yet. Bought this book? Share your experience and help other readers.
            </p>
          )}
          <Button
            onClick={() => setDialogOpen(true)}
            className="mt-4 w-full rounded-md bg-primary hover:bg-secondary text-white text-sm font-semibold gap-2"
            data-testid="write-review-btn"
          >
            <PenLine size={15} /> Write a review
          </Button>
        </div>

        {/* Review list */}
        <div className="md:col-span-8 space-y-4" data-testid="reviews-list">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <div key={i} className="skeleton h-24 w-full rounded-lg" />)}
            </div>
          ) : items.length === 0 ? null : (
            <>
              {items.map((r) => (
                <article key={r.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`review-card-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <StarIcons value={r.rating} size={14} />
                    <span className="text-xs text-slate-400">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : ""}
                    </span>
                  </div>
                  {r.title && <h3 className="mt-1.5 font-semibold text-sm text-slate-900">{r.title}</h3>}
                  <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{r.body}</p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{r.name}</span>
                    {r.verified_purchase && (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid={`review-verified-${r.id}`}>
                        <BadgeCheck size={13} /> Verified purchase
                      </span>
                    )}
                  </div>
                </article>
              ))}
              {hasMore && (
                <Button
                  variant="outline"
                  onClick={() => load(page + 1, true)}
                  className="rounded-md text-sm"
                  data-testid="reviews-load-more"
                >
                  Load more reviews ({total - items.length} remaining)
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Write review dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Review “{book.title}”</DialogTitle>
            <DialogDescription>
              Your review helps other readers choose their next book.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Your rating *</label>
              <StarPicker value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
            </div>
            {!user && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Name *</label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane D." data-testid="review-name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email *</label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="review-email" />
                  <p className="text-[11px] text-slate-400 mt-1">Never shown publicly.</p>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Headline (optional)</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Summarize your experience" maxLength={120} data-testid="review-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Your review *</label>
              <Textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} maxLength={2000} placeholder="What did you think of this book?" data-testid="review-body" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-md bg-primary hover:bg-secondary text-white text-sm font-semibold" data-testid="review-submit">
              {submitting ? "Submitting…" : "Submit review"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
